/**
 * HomeHero — persona-aware greeting strip + "what changed today"
 * digest + live cost-savings ticker.
 *
 * Used by both the CAIO and CEO home dashboards. Pure presentational:
 * derives all copy from useDashboardData() + useAuth().
 *
 *  ┌────────────────────────────────────────────────────────────────┐
 *  │  Good morning, Jordan.                                         │
 *  │  2 high-risk agents need your sign-off today.                  │
 *  │                                                                │
 *  │  📊 Today: +2 governed · 1 incident closed · 3 packs verified  │
 *  │  💰 ALGOS has saved Acme Bank A$284,612 this quarter           │
 *  └────────────────────────────────────────────────────────────────┘
 */
import React, { useEffect, useMemo, useState } from "react";
import { Sparkles, ArrowUpRight, Sun, Moon, Sunrise } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useDashboardData } from "@/contexts/DashboardDataContext";

const fmtFullAud = (v) =>
  new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  }).format(v);

const ROLE_PROMPTS = {
  CAIO:       (ctx) => ctx.pendingApprovals > 0
                        ? `${ctx.pendingApprovals} request${ctx.pendingApprovals === 1 ? "" : "s"} need your sign-off today.`
                        : "Estate clear. Time to look at the next wave of opportunities.",
  CIO:        (ctx) => ctx.openHigh > 0
                        ? `${ctx.openHigh} high-severity incident${ctx.openHigh === 1 ? "" : "s"} open across the estate.`
                        : "All systems healthy. No active incidents.",
  CRO:        (ctx) => ctx.highRiskAgents > 0
                        ? `${ctx.highRiskAgents} high-risk agent${ctx.highRiskAgents === 1 ? "" : "s"} flagged for review.`
                        : "Risk posture stable across the portfolio.",
  CEO:        (ctx) => `Estate ROI ${ctx.roi.toFixed(1)}× · ${ctx.governed} governed agent${ctx.governed === 1 ? "" : "s"} live.`,
  "IT Sec":   (ctx) => ctx.shadow > 0
                        ? `${ctx.shadow} shadow AI tool${ctx.shadow === 1 ? "" : "s"} detected. Bring under governance.`
                        : "No shadow AI signals in the last 7 days.",
  Legal:      () => "Policy library current. Review the open compliance gaps.",
  "DS Lead":  (ctx) => `${ctx.drifting} drift signal${ctx.drifting === 1 ? "" : "s"} active across the estate.`,
  "BU Head":  () => "Submit your next AI opportunity from the Identify menu.",
  Auditor:    () => "Audit chain verified · today's evidence packs ready.",
  "Exec Viewer": (ctx) => `Estate ROI ${ctx.roi.toFixed(1)}× · ${ctx.governed} governed agents live.`,
};

function greetingByHour(hour) {
  if (hour < 5) return { word: "Working late", Icon: Moon };
  if (hour < 12) return { word: "Good morning", Icon: Sunrise };
  if (hour < 18) return { word: "Good afternoon", Icon: Sun };
  return { word: "Good evening", Icon: Moon };
}

// Deterministic "today's digest" — derived from kpis so it stays
// stable across re-renders and the same per persona/tenant.
function buildDigest(kpis, shadowCount) {
  const newToday = Math.max(1, Math.round((kpis.governedAgents || 12) / 8));
  const closedToday = Math.max(0, Math.round((kpis.openHighIncidents || 0) / 2));
  const packsVerified = 3;
  const driftHandled = Math.max(0, Math.min(kpis.violations || 0, 5));
  const parts = [
    `+${newToday} new governed agent${newToday === 1 ? "" : "s"}`,
    `${closedToday} incident${closedToday === 1 ? "" : "s"} closed`,
    `${packsVerified} evidence pack${packsVerified === 1 ? "" : "s"} verified`,
    shadowCount > 0 && `${shadowCount} shadow AI item${shadowCount === 1 ? "" : "s"} flagged`,
    driftHandled > 0 && `${driftHandled} policy alert${driftHandled === 1 ? "" : "s"} resolved`,
  ].filter(Boolean);
  return parts.slice(0, 4);
}

// Quarter-to-date savings — derived as a function of monthly burn so it
// scales with whichever tenant is active. Increments live for demo flair.
function useLiveSavings(kpis) {
  const baseSavings = useMemo(() => {
    const burn = kpis.monthlySpendAud || 0;
    // ~ 9.2% of QTD burn saved via optimisations — feels real for the demo.
    const qtdBurn = burn * 3;
    return Math.round(qtdBurn * 0.092);
  }, [kpis.monthlySpendAud]);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + Math.floor(Math.random() * 7) + 1), 1500);
    return () => clearInterval(t);
  }, []);
  return baseSavings + tick;
}

export default function HomeHero({ subhead }) {
  const { user } = useAuth();
  const { kpis, shadowAgents, agents, tenant } = useDashboardData();
  const shadowCount = (shadowAgents || []).length;
  const firstName = user?.name?.split(" ")[0] || "there";
  const { word, Icon } = greetingByHour(new Date().getHours());

  const ctx = {
    pendingApprovals: kpis.pendingApprovals || 0,
    openHigh: kpis.openHighIncidents || 0,
    highRiskAgents: (agents || []).filter((a) => a.riskBand === "high").length,
    roi: kpis.roiRatio || 0,
    governed: kpis.governedAgents || 0,
    shadow: shadowCount,
    drifting: (agents || []).filter((a) => a.driftSignal === "warning" || a.driftSignal === "alert").length,
  };
  const persona = ROLE_PROMPTS[user?.role] || ROLE_PROMPTS.CAIO;
  const personaLine = persona(ctx);

  const digest = useMemo(() => buildDigest(kpis, shadowCount), [kpis, shadowCount]);
  const savings = useLiveSavings(kpis);

  return (
    <div
      className="surface-card shadow-e1 overflow-hidden mt-6"
      data-testid="home-hero"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
        {/* LEFT — greeting (8/12) */}
        <div className="lg:col-span-8 p-5 border-b lg:border-b-0 lg:border-r border-border-subtle bg-gradient-to-br from-tralenex-50/40 to-transparent">
          <div className="flex items-center gap-2">
            <Icon size={13} className="text-tralenex-700" />
            <p className="eyebrow text-tralenex-700">{word}</p>
          </div>
          <h2 className="text-display-sm text-ink-primary mt-1 leading-tight" data-testid="home-hero-greeting">
            {word}, {firstName}.
          </h2>
          <p className="text-ink-secondary mt-2 max-w-2xl" data-testid="home-hero-persona-line">
            {personaLine}
          </p>
          {subhead && <p className="text-meta text-ink-disabled mt-2">{subhead}</p>}

          <div className="mt-4 inline-flex flex-wrap items-center gap-2" data-testid="home-hero-digest">
            <span className="inline-flex items-center gap-1 text-meta font-bold tracking-[0.08em] uppercase text-tralenex-700">
              <Sparkles size={11} /> Today
            </span>
            {digest.map((d, i) => (
              <React.Fragment key={d}>
                {i > 0 && <span className="text-ink-disabled">·</span>}
                <span className="text-sm text-ink-primary">{d}</span>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* RIGHT — live savings (4/12) */}
        <div className="lg:col-span-4 p-5 flex flex-col justify-center bg-gradient-to-bl from-risk-low/10 to-transparent" data-testid="home-hero-savings">
          <p className="eyebrow text-risk-low inline-flex items-center gap-1">
            <ArrowUpRight size={11} /> Savings QTD
          </p>
          <p className="text-display-md text-ink-primary mt-1 tabular-nums leading-none" data-testid="home-hero-savings-value">
            {fmtFullAud(savings)}
          </p>
          <p className="text-meta text-ink-secondary mt-2 max-w-[28ch] leading-snug">
            ALGOS has trimmed {tenant?.name || "this tenant"}&apos;s AI run-cost via
            model-swap nudges, autonomy down-grades and drift fixes this quarter.
          </p>
          <div className="mt-3 flex items-center gap-1">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-risk-low animate-pulse" />
            <span className="text-[10px] uppercase tracking-[0.08em] font-bold text-risk-low">Live</span>
          </div>
        </div>
      </div>
    </div>
  );
}
